<template>
  <div class="confirmation-container" :class="{ 'compact-view': asistenciaConfirmada }">
    <div class="stars-container">
      <div v-for="n in 30" :key="'s'+n" class="star" :style="generateStarStyle()"></div>
    </div>

    <div ref="bienvenidaRef" class="glass-card shadow-2xl" :class="{ 'compact-card': asistenciaConfirmada }">
      
      <!-- ✅ MENSAJE FINAL -->
      <div v-if="asistenciaConfirmada" class="qr-view text-center">
        <div class="ticket-header">
          <h2 class="gold-text">Gracias por confirmar tu asistencia</h2>
        </div>
      </div>

      <!-- FORMULARIO -->
      <div v-else>
        <div class="form-header">
          <h2 class="gold-title">Confirmar Asistencia</h2>
          <p class="white-subtitle">Tu presencia hará este día más especial</p>
        </div>
        
        <form @submit.prevent="handleSubmit" class="modern-form">
          <div class="form-group">
            <label class="modern-label">Nombre Completo</label>
            <input
              v-model.trim="name"
              type="text"
              class="modern-input"
              placeholder="Ej. Juan Soto"
              required
            >
          </div>

          <div class="form-group">
            <label class="modern-label">WhatsApp / Teléfono</label>
            <input
              v-model.trim="phone"
              type="tel"
              class="modern-input"
              placeholder="10 dígitos"
              required
              pattern="[0-9]{10}"
            >
          </div>

          <div v-if="!isXvEvent" class="form-group">
            <label class="modern-label">Invitado por:</label>
            <div class="pill-group">
              <label class="pill-item">
                <input v-model="invitedBy" type="radio" value="novia" required>
                <span>La Novia</span>
              </label>
              <label class="pill-item">
                <input v-model="invitedBy" type="radio" value="novio">
                <span>El Novio</span>
              </label>
            </div>
          </div>

          <div class="form-group">
            <label class="modern-label">¿Nos acompañas?</label>
            <div class="pill-group">
              <label class="pill-item accent-si">
                <input v-model="attending" type="radio" value="si" required>
                <span>¡Sí, asistiré!</span>
              </label>
              <label class="pill-item accent-no">
                <input v-model="attending" type="radio" value="no">
                <span>No podré ir</span>
              </label>
            </div>
          </div>

          <div class="form-group" v-if="attending === 'si'">
            <label class="modern-label">Lugares a confirmar:</label>
            <div class="select-wrapper">
              <select v-model="guests" class="modern-input" required>
                <option value="" disabled>Selecciona cantidad</option>
                <option v-for="n in guestsOptions" :key="n" :value="n">
                  {{ n }} persona{{ n > 1 ? 's' : '' }}
                </option>
              </select>
            </div>
          </div>
          
          <button type="submit" class="btn-confirm-gold" :disabled="isSubmitting">
            <span v-if="!isSubmitting">CONFIRMAR</span>
            <div v-else class="loader-dots"><span>.</span><span>.</span><span>.</span></div>
          </button>

          <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
        </form>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue';
import axios from 'axios';
import baseUrl from '../api';

export default {
  name: 'AttendanceConfirmation',
  props: {
    idInvitado: { type: Number, required: true },
    nombre: { type: String, default: '' },
    idFestejado: { type: Number, default: null }
  },
  setup(props) {
    const name = ref('');
    const phone = ref('');
    const invitedBy = ref('');
    const attending = ref('');
    const guests = ref('');
    const guestsOptions = ref([1, 2]);
    const loading = ref(true);
    const isSubmitting = ref(false);
    const errorMessage = ref('');
    const asistenciaConfirmada = ref(false);

    const generateStarStyle = () => ({
      top: `${Math.random() * 100}%`,
      left: `${Math.random() * 100}%`,
      width: `${Math.random() * 2 + 1}px`,
      height: `${Math.random() * 2 + 1}px`,
      animationDuration: `${Math.random() * 3 + 2}s`
    });

    const isXvEvent = computed(() =>
      ['xv', 'ym'].includes(props.nombre?.toLowerCase())
    );

    const fetchGuestLimit = async () => {
      try {
        const response = await axios.get(
          `${baseUrl}/${props.nombre ? `${props.nombre}/` : ''}${props.idFestejado ? `${props.idFestejado}/` : ''}${props.idInvitado}`
        );
        const numPases = response.data.num_pase;
        guestsOptions.value = numPases > 0
          ? Array.from({ length: numPases }, (_, i) => i + 1)
          : [1, 2];
      } catch (e) {
        console.error(e);
      } finally {
        loading.value = false;
      }
    };

    const handleSubmit = async () => {
      isSubmitting.value = true;
      try {
        const formData = {
          nombre_asistencia: name.value.trim(),
          telefono_invitado: phone.value.trim(),
          invitedby: isXvEvent.value ? 'novia' : invitedBy.value,
          asistira: attending.value,
          id_festejada: props.idFestejado,
          id_invitado: props.idInvitado,
          num_pases:
            attending.value === 'no' ? 0 : Number(guests.value),
        };

        await axios.post(`${baseUrl}/asistencia`, formData);

        asistenciaConfirmada.value = true;
      } catch (e) {
        errorMessage.value =
          'Error al confirmar. Intenta de nuevo.';
      } finally {
        isSubmitting.value = false;
      }
    };

    watch(attending, (val) => {
      if (val === 'no') guests.value = '0';
    });

    onMounted(() => {
      fetchGuestLimit();
    });

    return {
      name,
      phone,
      invitedBy,
      attending,
      guests,
      guestsOptions,
      loading,
      isSubmitting,
      errorMessage,
      isXvEvent,
      asistenciaConfirmada,
      handleSubmit,
      generateStarStyle,
    };
  },
};
</script>

<style scoped>
.confirmation-container {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
background-image: 
  linear-gradient(to bottom, 
    rgba(0, 0, 0, 0.516) 60%, 
    rgba(26, 29, 81, 0.7) 85%, 
    rgba(31, 33, 61, 0.461) 100%
  ),
  url('@/assets/Diseño sin título (7) - copia.jpg');   overflow: hidden;
}

/* --- ESTRELLAS --- */
.star {
  position: absolute;
  background: white;
  border-radius: 50%;
  animation: twinkle linear infinite;
}
@keyframes twinkle { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }

/* --- CARD GLASS --- */
.glass-card {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 480px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 30px;
  padding: 40px;
}

.gold-title {
  font-family: 'nombre', cursive;
  color: #a48250;
  font-size: 2rem;
  margin-bottom: 5px;
  text-align: center;
}

.white-subtitle {
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  font-size: 0.95rem;
  margin-bottom: 30px;
}

/* --- INPUTS --- */
.modern-label {
  display: block;
  color: #a48250;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  font-weight: 600;
}

.modern-input {
  width: 100%;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 12px 15px;
  color: white;
  transition: all 0.3s;
}

.modern-input:focus {
  outline: none;
  border-color: #a48250;
  background: rgba(255, 255, 255, 0.12);
}

/* --- PILLS (RADIOS) --- */
.pill-group {
  display: flex;
  gap: 10px;
}

.pill-item {
  flex: 1;
  cursor: pointer;
  position: relative;
}

.pill-item input { display: none; }

.pill-item span {
  display: block;
  text-align: center;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50px;
  color: white;
  font-size: 0.9rem;
  transition: all 0.3s;
}

.pill-item input:checked + span {
  background: #a48250;
  color: black;
  font-weight: bold;
  box-shadow: 0 0 15px rgba(164, 130, 80, 0.4);
}

/* --- TICKET VIP / QR --- */
.qr-view {
  animation: slideUp 0.5s ease-out;
}

.gold-text { color: #a48250; font-family: 'Westony', serif; font-size: 2rem; }

.qr-scanner-effect {
  position: relative;
  display: inline-block;
  margin: 20px 0;
  padding: 15px;
  background: white;
  border-radius: 20px;
}

.qr-code { width: 200px; height: 200px; }

.scanner-line {
  position: absolute;
  top: 0; left: 0; width: 100%; height: 2px;
  background: #a48250;
  box-shadow: 0 0 10px #a48250;
  animation: scan 2s linear infinite;
}

@keyframes scan {
  0% { top: 5%; }
  50% { top: 95%; }
  100% { top: 5%; }
}

/* --- BOTÓN --- */
.btn-confirm-gold {
  width: 100%;
  margin-top: 20px;
  padding: 18px;
  border-radius: 50px;
  border: none;
  background: linear-gradient(to bottom, #000000 0%, #08062d 100%);
  color: rgb(243, 238, 238);
  font-weight: 900;
  letter-spacing: 1px;
  cursor: pointer;
  transition: 0.3s;
}

.btn-confirm-gold:hover {
  transform: scale(1.02);
  box-shadow: 0 5px 20px rgba(164, 130, 80, 0.4);
}

.loader-dots span {
  animation: blink 1s infinite;
  margin: 0 2px;
}

@keyframes blink { 0%, 100% { opacity: 0; } 50% { opacity: 1; } }

@media (max-width: 480px) {
  .glass-card { padding: 30px 20px; }
  .gold-title { font-size: 1.8rem; }
}

.guest-name {
  color: white;
  font-weight: 600;
  margin-top: 15px;
    font-family: 'Playfair Display', serif;

}

.qr-instructions {
  color: white;
  opacity: 0.9;
    font-family: 'Playfair Display', serif;

  font-size: 0.9rem;
  margin-top: 5px;
}
.food-note{
  margin-top: 18px;
  font-size: 0.9rem;
  line-height: 1.6;
  color: rgba(255,255,255,0.85);
  font-family: 'Playfair Display', serif;
}

.small-note{
  font-size: 0.8rem;
  opacity: 0.7;
  margin-top: 10px;
}

.btn-whatsapp{
  display: block;
  margin: 18px auto 10px;
  padding: 14px 18px;
  border-radius: 40px;
  background: linear-gradient(to right, #25D366, #1ebe5d);
  color: white;
  text-align: center;
  font-weight: 700;
  text-decoration: none;
  transition: 0.3s;
  max-width: 280px;
}

.btn-whatsapp:hover{
  transform: scale(1.05);
  box-shadow: 0 5px 20px rgba(37, 211, 102, 0.4);
}
</style>